import { useEffect, useState, useCallback } from "react";
import { Box, Flex, Text, Button, Stack, HStack, Badge, Spinner, Input } from "@chakra-ui/react";
import { Plus, Users, Mail, Copy, KeyRound, UserX, UserCheck, Trash2 } from "lucide-react";
import { apiGet, apiPost } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { ConfirmModal } from "@/components/ConfirmModal";
import { BulkInviteModal } from "@/components/BulkInvite";
import { colors } from "@/theme/colors";

const ROLE_LABELS = { org_admin: "Org Admin", supervisor: "Supervisor", field_employee: "Field Employee" };
const ROLE_ORDER = ["org_admin", "supervisor", "field_employee"];
const STATUS_META = {
  active: { bg: colors.resolvedBg, color: colors.resolvedText, label: "Active" },
  pending: { bg: colors.pendingBg, color: colors.pendingText, label: "Pending invite" },
  inactive: { bg: "#ECECEC", color: colors.textSecondary, label: "Deactivated" },
};
const fmtLast = (ts) => (ts ? new Date(ts).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "Never signed in");

const RoleSelect = ({ value, onChange, testId }) => (
  <Box as="select" value={value} onChange={onChange} data-testid={testId}
    px="2.5" py="1.5" borderRadius="8px" border="1px solid" borderColor={colors.border} bg={colors.white} fontSize="xs">
    {ROLE_ORDER.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
  </Box>
);

const ActionBtn = ({ onClick, loading, color, children, testId }) => (
  <Button size="xs" variant="ghost" color={color || colors.tealHover} loading={loading} onClick={onClick} data-testid={testId}>
    {children}
  </Button>
);

function UserRow({ u, companyId, busy, onAction }) {
  const meta = STATUS_META[u.status] || { bg: colors.cream, color: colors.textSecondary, label: u.status };
  const pending = u.status === "pending";
  const inactive = u.status === "inactive";
  return (
    <Flex justify="space-between" align="flex-start" gap="3" flexWrap="wrap" py="3"
      borderBottom="1px solid" borderColor={colors.border} _last={{ borderBottom: "none" }}
      data-testid={`user-row-${u.user_id}`}>
      <Box minW="0">
        <HStack gap="2" flexWrap="wrap">
          <Text fontWeight="600" fontSize="sm" color={colors.textPrimary}>{u.full_name || "(no name yet)"}</Text>
          <Badge bg={meta.bg} color={meta.color} borderRadius="full" px="2" fontSize="10px" data-testid={`user-status-${u.user_id}`}>{meta.label}</Badge>
        </HStack>
        <Text fontSize="xs" color={colors.textSecondary}>{u.email}</Text>
        <Text fontSize="xs" color={colors.textSecondary}>Last active: {fmtLast(u.last_sign_in_at)}</Text>
      </Box>
      <Stack gap="1.5" align="flex-end">
        <RoleSelect value={u.role} testId={`user-role-select-${u.user_id}`}
          onChange={(e) => onAction("role", u, e.target.value)} />
        <HStack gap="0" flexWrap="wrap" justify="flex-end">
          {pending && (
            <ActionBtn onClick={() => onAction("resend", u)} loading={busy === `resend-${u.user_id}`} testId={`resend-invite-${u.user_id}`}>
              <Mail size={13} /> Resend invite
            </ActionBtn>
          )}
          <ActionBtn onClick={() => onAction("reset", u)} loading={busy === `reset-${u.user_id}`} testId={`password-reset-${u.user_id}`}>
            <KeyRound size={13} /> Reset password
          </ActionBtn>
          {inactive && (
            <ActionBtn onClick={() => onAction("reactivate", u)} loading={busy === `reactivate-${u.user_id}`} testId={`reactivate-user-${u.user_id}`}>
              <UserCheck size={13} /> Reactivate
            </ActionBtn>
          )}
          {!inactive && !pending && (
            <ActionBtn color={colors.poppy} onClick={() => onAction("deactivate", u)} testId={`deactivate-user-${u.user_id}`}>
              <UserX size={13} /> Deactivate
            </ActionBtn>
          )}
          <ActionBtn color={colors.poppy} onClick={() => onAction("remove", u)} testId={`remove-user-${u.user_id}`}>
            <Trash2 size={13} /> Remove
          </ActionBtn>
        </HStack>
      </Stack>
    </Flex>
  );
}

const GroupHeading = ({ children, count, testId }) => (
  <HStack gap="2" mt="1">
    <Text fontSize="xs" fontWeight="700" letterSpacing="0.06em" textTransform="uppercase" color={colors.textSecondary} data-testid={testId}>{children}</Text>
    <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2" fontSize="10px">{count}</Badge>
  </HStack>
);

const InviteLinkCard = ({ inviteLink, onDismiss, onCopy }) => (
  <Box mb="3" p="3" borderRadius="10px" border="1px solid" borderColor={colors.teal} bg={colors.tealTint} data-testid="user-invite-link-card">
    <Flex justify="space-between" gap="2" align="center" mb="1.5">
      <Text fontSize="xs" fontWeight="600" color={colors.textPrimary}>Invite link for {inviteLink.email}{inviteLink.emailed ? " (email sent)" : ""}</Text>
      <Button size="xs" variant="ghost" onClick={onDismiss} data-testid="dismiss-user-invite">Dismiss</Button>
    </Flex>
    <HStack gap="2">
      <Input size="sm" readOnly value={inviteLink.link} bg={colors.white} borderColor={colors.border} fontSize="xs" data-testid="user-invite-link-input" />
      <Button size="sm" variant="outline" onClick={onCopy} data-testid="copy-user-invite-link">
        <Copy size={13} /> Copy
      </Button>
    </HStack>
  </Box>
);

const InviteUserModal = ({ open, onClose, form, setForm, busy, onSubmit }) => (
  <Modal open={open} onClose={onClose} title="Invite user" testId="invite-user-modal">
    <Stack gap="3">
      <Box>
        <Text fontSize="sm" fontWeight="500" mb="1.5" color={colors.textPrimary}>Full name (optional)</Text>
        <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} bg={colors.white} borderColor={colors.border} data-testid="invite-name-input" />
      </Box>
      <Box>
        <Text fontSize="sm" fontWeight="500" mb="1.5" color={colors.textPrimary}>Email *</Text>
        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} bg={colors.white} borderColor={colors.border} data-testid="invite-email-input" />
      </Box>
      <Box>
        <Text fontSize="sm" fontWeight="500" mb="1.5" color={colors.textPrimary}>Role</Text>
        <Box as="select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
          w="full" px="3" py="2.5" borderRadius="8px" border="1px solid" borderColor={colors.border} bg={colors.white} fontSize="sm" data-testid="invite-role-select">
          {ROLE_ORDER.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
        </Box>
      </Box>
      <Button onClick={onSubmit} loading={busy} disabled={!form.email.trim()} data-testid="send-invite-btn">Send invite</Button>
    </Stack>
  </Modal>
);

// Per-user platform actions — extracted so the main component stays lean.
const useUserActions = ({ companyId, flash, refresh, setInviteLink }) => {
  const acceptInviteUrl = (token) => `${window.location.origin}/accept-invite?token_hash=${token}&type=recovery`;

  const changeRole = async (u, role) => {
    await apiPost(`/platform/companies/${companyId}/users/${u.user_id}/role`, { role });
    flash("success", `${u.full_name || u.email} is now ${ROLE_LABELS[role]}.`);
    refresh();
  };

  const resendInvite = async (u) => {
    const res = await apiPost(`/platform/companies/${companyId}/users/${u.user_id}/resend-invite`, {
      redirect_to: `${window.location.origin}/accept-invite`,
    });
    if (!res.hashed_token) {
      flash("error", `Could not generate a new link for ${u.email} right now (rate limit) — try again in a minute.`);
      return;
    }
    setInviteLink({ email: u.email, link: acceptInviteUrl(res.hashed_token), emailed: res.emailed });
    flash("success", res.emailed ? `Invite re-sent to ${u.email} — link below as backup.` : `New invite link generated for ${u.email}.`);
  };

  const resetPassword = async (u) => {
    const res = await apiPost(`/platform/companies/${companyId}/users/${u.user_id}/password-reset`, {
      redirect_to: `${window.location.origin}/reset-password`,
    });
    flash("success", res.emailed ? `Password reset email sent to ${u.email}.` : `Password reset link generated for ${u.email}.`);
  };

  const reactivate = async (u) => {
    await apiPost(`/platform/companies/${companyId}/users/${u.user_id}/status`, { active: true });
    flash("success", `${u.full_name || u.email} reactivated — access restored.`);
    refresh();
  };

  return { ACTIONS: { role: changeRole, resend: resendInvite, reset: resetPassword, reactivate }, acceptInviteUrl };
};

export function CompanyUsers({ companyId, companyName, flash, onChanged }) {
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ full_name: "", email: "", role: "field_employee" });
  const [inviting, setInviting] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [confirm, setConfirm] = useState(null); // { type: 'deactivate'|'remove', user }
  const [attachConfirm, setAttachConfirm] = useState(null); // { email, full_name, role }

  const load = useCallback(async () => {
    try {
      const res = await apiGet(`/platform/companies/${companyId}/users`);
      setUsers(res.users || []);
    } catch (e) { setUsers([]); }
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged?.(); };

  const invite = async () => {
    const email = inviteForm.email.trim().toLowerCase();
    if (!email) { flash("error", "Email is required."); return; }
    setInviting(true);
    try {
      const res = await apiPost(`/platform/companies/${companyId}/users/invite`, {
        email, full_name: inviteForm.full_name.trim() || null, role: inviteForm.role,
        redirect_to: `${window.location.origin}/accept-invite`,
      });
      if (res.hashed_token) {
        setInviteLink({ email, link: acceptInviteUrl(res.hashed_token), emailed: res.emailed });
        flash("success", `${email} invited as ${ROLE_LABELS[inviteForm.role]}${res.emailed ? " — invite email sent." : " — share the link below."}`);
      } else {
        flash("success", `${email} invited as ${ROLE_LABELS[inviteForm.role]} — link could not be generated right now (rate limit); use Resend invite in a minute.`);
      }
      setInviteOpen(false);
      setInviteForm({ full_name: "", email: "", role: "field_employee" });
      refresh();
    } catch (e) {
      if (e.detail?.code === "exists_other") {
        setAttachConfirm({ email, full_name: inviteForm.full_name.trim() || null, role: inviteForm.role });
      } else {
        flash("error", e.message);
      }
    } finally { setInviting(false); }
  };

  // Super-Admin confirmed: attach a membership to the EXISTING login — no password link.
  const attachExisting = async () => {
    const { email, full_name, role } = attachConfirm;
    setInviting(true);
    try {
      await apiPost(`/platform/companies/${companyId}/users/invite`, {
        email, full_name, role, attach_existing: true,
        redirect_to: `${window.location.origin}/accept-invite`,
      });
      flash("success", `${email} added to ${companyName || "this company"} as ${ROLE_LABELS[role]} — they keep their existing login and password.`);
      setAttachConfirm(null);
      setInviteOpen(false);
      setInviteForm({ full_name: "", email: "", role: "field_employee" });
      refresh();
    } catch (e) { flash("error", e.message); } finally { setInviting(false); }
  };

  const { ACTIONS, acceptInviteUrl } = useUserActions({ companyId, flash, refresh, setInviteLink });

  const onAction = async (type, u, value) => {
    if (type === "deactivate" || type === "remove") { setConfirm({ type, user: u }); return; }
    setBusy(`${type}-${u.user_id}`);
    try { await ACTIONS[type](u, value); } catch (e) { flash("error", e.message); }
    setBusy(null);
  };

  const runConfirm = async () => {
    const { type, user: u } = confirm;
    setBusy(`confirm-${u.user_id}`);
    try {
      if (type === "deactivate") {
        await apiPost(`/platform/companies/${companyId}/users/${u.user_id}/status`, { active: false });
        flash("success", `${u.full_name || u.email} deactivated — they can no longer sign in to this company.`);
      } else {
        await apiPost(`/platform/companies/${companyId}/users/${u.user_id}/remove`, {});
        flash("success", `${u.full_name || u.email} removed from the company. Their past records remain intact.`);
      }
      refresh();
    } catch (e) { flash("error", e.message); }
    setBusy(null);
    setConfirm(null);
  };

  if (users === null) return <Flex py="6" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>;

  const pending = users.filter((u) => u.status === "pending");
  const rest = users.filter((u) => u.status !== "pending");
  const byRole = ROLE_ORDER.map((r) => ({ role: r, list: rest.filter((u) => u.role === r) })).filter((g) => g.list.length);

  return (
    <Box data-testid="company-users">
      <Flex justify="space-between" align="center" mb="2" gap="2" flexWrap="wrap">
        <Text fontSize="sm" color={colors.textSecondary}>{users.length} {users.length === 1 ? "user" : "users"} in this company</Text>
        <HStack gap="2">
          <Button size="sm" variant="outline" onClick={() => { setInviteLink(null); setBulkOpen(true); }} data-testid="bulk-invite-btn">
            <Users size={15} /> Bulk invite
          </Button>
          <Button size="sm" onClick={() => { setInviteLink(null); setInviteOpen(true); }} data-testid="invite-user-btn">
            <Plus size={15} /> Invite user
          </Button>
        </HStack>
      </Flex>

      {inviteLink && (
        <InviteLinkCard inviteLink={inviteLink} onDismiss={() => setInviteLink(null)}
          onCopy={() => { navigator.clipboard?.writeText(inviteLink.link); flash("success", "Invite link copied."); }} />
      )}

      {users.length === 0 ? (
        <Text fontSize="sm" color={colors.textSecondary} data-testid="users-empty">No users yet — invite the first one.</Text>
      ) : (
        <Stack gap="4">
          {pending.length > 0 && (
            <Box>
              <GroupHeading count={pending.length} testId="pending-invites-group">Pending invites</GroupHeading>
              {pending.map((u) => <UserRow key={u.user_id} u={u} companyId={companyId} busy={busy} onAction={onAction} />)}
            </Box>
          )}
          {byRole.map((g) => (
            <Box key={g.role}>
              <GroupHeading count={g.list.length} testId={`role-group-${g.role}`}>{ROLE_LABELS[g.role]}s</GroupHeading>
              {g.list.map((u) => <UserRow key={u.user_id} u={u} companyId={companyId} busy={busy} onAction={onAction} />)}
            </Box>
          ))}
        </Stack>
      )}

      <InviteUserModal open={inviteOpen} onClose={() => setInviteOpen(false)}
        form={inviteForm} setForm={setInviteForm} busy={inviting} onSubmit={invite} />

      <BulkInviteModal open={bulkOpen} onClose={() => setBulkOpen(false)}
        companyId={companyId} flash={flash} onDone={refresh} />

      <ConfirmModal open={!!confirm} onClose={() => setConfirm(null)}
        title={confirm ? (confirm.type === "deactivate" ? `Deactivate ${confirm.user.full_name || confirm.user.email}?` : `Remove ${confirm.user.full_name || confirm.user.email}?`) : ""}
        body={confirm ? (confirm.type === "deactivate"
          ? "They immediately lose access to this company. Their history is kept and you can reactivate them at any time."
          : "This revokes their membership in this company. Their past submissions and records remain intact and attributed to them.") : ""}
        confirmLabel={confirm?.type === "deactivate" ? "Deactivate" : "Remove"} danger
        busy={busy?.startsWith("confirm-")} onConfirm={runConfirm} testId="user-confirm-modal" />

      <ConfirmModal open={!!attachConfirm} onClose={() => setAttachConfirm(null)}
        title="Add existing account?"
        body={attachConfirm ? `This email already belongs to an existing Hakina account. Add them to ${companyName || "this company"} as a new ${ROLE_LABELS[attachConfirm.role]} membership? They keep their one existing login and password — no password-set email is sent.` : ""}
        confirmLabel="Add membership"
        busy={inviting} onConfirm={attachExisting} testId="attach-existing-modal" />
    </Box>
  );
}
